<template>
  <div class="flex flex-col h-full fade-in">
    <!-- Header section -->
    <DashboardHeader title="Recently Active Applicants" link="/applicants">
      <template #right>
        <div class="mb-2 flex items-center gap-2 flex-wrap">
      <span class="text-xs text-gray-500 font-medium">Active in:</span>
      <Select v-model="selectedTimeframe" @update:model-value="onTimeframeChange">
        <SelectTrigger
          class="h-8 rounded-full px-3 text-xs font-medium border bg-white hover:bg-gray-50 transition-colors w-auto gap-1.5 border-cosmicPurple/40 bg-cosmicPurple/5 text-cosmicPurple"
        >
          <Clock class="w-3 h-3 shrink-0" />
          <SelectValue
            :placeholder="`Last ${selectedTimeframe} month${selectedTimeframe > 1 ? 's' : ''}`"
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem :value="1">1 month</SelectItem>
          <SelectItem :value="2">2 months</SelectItem>
          <SelectItem :value="3">3 months</SelectItem>
          <SelectItem :value="6">6 months</SelectItem>
        </SelectContent>
      </Select>
    </div>
      </template>
    </DashboardHeader>
    

    <!-- Main content section -->
    <div class="flex w-full flex-1 overflow-y-auto fade-in">
      <div class="w-full">
        <div v-if="latestUpdatedApplicantsLoading" class="flex justify-center py-4">
          <Loader2 class="w-6 h-6 animate-spin" />
        </div>
        <div
          v-else-if="latestUpdatedApplicantsError"
          class="text-red-500 py-4 text-center rounded-xl border-[1px] border-gray-200 bg-white p-4 overflow-hidden"
        >
          {{ latestUpdatedApplicantsError }}
        </div>
        <div
          v-else-if="latestUpdatedApplicants.length"
          class="overflow-hidden"
        >
          <div class="rounded-xl border-[1px] border-gray-200 overflow-hidden">
            <table class="w-full">
              <thead>
                <tr class="bg-gray-50 border-b border-gray-200">
                  <th
                    class="p-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-10"
                  ></th>
                  <th
                    class="p-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
                  >
                    Applicant
                  </th>
                  <th
                    class="p-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
                  >
                    Batch
                  </th>
                  <th
                    class="p-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer"
                    @click="handleSort('activity_date')"
                  >
                    <div class="flex items-center gap-1">
                      Last Update
                      <ChevronUp v-if="currentSort === 'activity_date'" class="w-4 h-4" />
                      <ChevronDown v-if="currentSort === '-activity_date'" class="w-4 h-4" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr
                  v-for="applicant in latestUpdatedApplicants"
                  :key="applicant.user_details.id"
                  class="hover:bg-gray-50 transition-colors duration-200 cursor-pointer bg-white"
                  @click="handleRowClick($event, applicant)"
                >
                  <!-- Avatar -->
                  <td class="p-3">
                    <div
                      v-if="applicant.user_details?.avatar_image"
                      class="h-10 min-w-10 aspect-square rounded-full overflow-hidden ring-1 ring-gray-200"
                    >
                      <img
                        :src="applicant.user_details?.avatar_image"
                        :alt="`${applicant.user_details?.first_name} ${applicant.user_details?.last_name}`"
                        class="h-10 min-w-10 aspect-square object-cover"
                        :class="{ 'privacy-blur': isBlurred }"
                      />
                    </div>
                    <div
                      v-else
                      class="h-10 min-w-10 aspect-square rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm"
                    >
                      <span :class="{ 'privacy-blur': isBlurred }">
                        {{ getInitials(applicant.user_details?.first_name, applicant.user_details?.last_name) }}
                      </span>
                    </div>
                  </td>

                  <!-- Applicant Name and Email -->
                  <td class="p-1">
                    <div class="font-medium text-sm" :class="{ 'privacy-blur': isBlurred }">
                      {{ applicant.user_details?.first_name }} {{ applicant.user_details?.last_name }}
                    </div>
                    <div class="text-sm text-gray-600" :class="{ 'privacy-blur': isBlurred }">
                      {{ applicant.user_details?.email }}
                    </div>
                  </td>

                  <!-- Batch -->
                  <td class="p-3 text-sm text-gray-600">
                    <template v-if="applicant.fellow_data?.formatted_batch">
                      {{ applicant.fellow_data.formatted_batch }}
                    </template>
                    <template v-else>
                      —
                    </template>
                  </td>

                  <!-- Last update -->
                  <td class="p-3 text-sm text-gray-600 whitespace-nowrap">
                    <div class="flex items-center">
                      <Clock class="inline-block w-3 h-3 mr-1" />
                      {{ formatRelativeTime(getApplicantUpdatedAt(applicant)) }}
                      <span class="ml-2 text-gray-400 hidden md:inline"
                        >({{ formatDateUtil(getApplicantUpdatedAt(applicant)) }})</span
                      >
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <!-- Pagination -->
          <PaginationComponent
            v-if="latestUpdatedApplicants.length && latestUpdatedTotalCount > pageSize"
            :current-page="currentPage"
            :total-pages="totalPages"
            :use-router="true"
            :additional-query-params="{
              timeframe: selectedTimeframe.toString(),
              ordering: currentSort
            }"
          />
        </div>
        <div
          v-else
          class="text-center py-4 text-gray-500 rounded-xl border-[1px] border-gray-200 bg-white p-4 overflow-hidden"
        >
          No recently updated referrals found.
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Clock, ChevronUp, ChevronDown, Loader2 } from 'lucide-vue-next'

import { useApplicantsStore } from '@/stores/applicants'
import type { Applicant } from '@/types/applicants'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import { formatDate as formatDateUtil } from '@/utils/formatDate'
import { useAuth } from '@/composables/useAuth'
import { usePrivacy } from '@/composables/usePrivacy'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import DashboardHeader from '@/components/common/DashboardHeader.vue'

const router = useRouter()
const route = useRoute()
const applicantsStore = useApplicantsStore()
const { isGuide } = useAuth()
const { isBlurred } = usePrivacy()

const selectedTimeframe = ref(3) // Default to 3 months
const pageSize = 50
const currentPage = ref(1)
const currentSort = ref('-activity_date') // Default sort

const latestUpdatedApplicants = computed(() => applicantsStore.latestUpdatedApplicants)
const latestUpdatedApplicantsLoading = computed(() => applicantsStore.loading)
const latestUpdatedApplicantsError = computed(() => applicantsStore.error)
const latestUpdatedTotalCount = computed(() => applicantsStore.latestUpdatedTotalCount)
const totalPages = computed(() => Math.ceil(latestUpdatedTotalCount.value / pageSize))

const handleRowClick = (event: MouseEvent, applicant: Applicant) => {
  if (isBlurred.value) {
    return
  }

  // Let the browser handle cmd/ctrl clicks naturally
  if (event.metaKey || event.ctrlKey) {
    const url = `/applicant/${applicant.user_details.id}${getQueryString()}`
    window.open(url, '_blank')
    return
  }
  
  // For normal clicks, use Vue Router
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push({
    path: `/applicant/${applicant.user_details.id}`,
    query: { ...route.query }
  })
}

const getApplicantUpdatedAt = (applicant: Applicant): string => {
  return applicant.latest_application?.updated_at || applicant.updated_at || ''
}

const getQueryString = () => {
  if (!route.query || Object.keys(route.query).length === 0) return ''
  
  const queryParams = new URLSearchParams()
  Object.entries(route.query).forEach(([key, value]) => {
    if (value) queryParams.append(key, value as string)
  })
  
  return `?${queryParams.toString()}`
}

const onTimeframeChange = () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push({
    query: {
      ...route.query,
      timeframe: selectedTimeframe.value.toString(),
      page: '1', // Reset to first page when changing timeframe
      ordering: currentSort.value // Maintain current sort
    }
  })
  currentPage.value = 1
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  applicantsStore.fetchLatestUpdatedApplicants(
    selectedTimeframe.value, 
    pageSize, 
    currentPage.value,
    currentSort.value
  )
}

// Handle sorting when a column header is clicked
const handleSort = (field: string) => {
  let newSort = field
  if (currentSort.value === field) {
    newSort = `-${field}`
  } else if (currentSort.value === `-${field}`) {
    newSort = field
  }
  
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push({
    query: {
      ...route.query,
      ordering: newSort,
      page: '1' // Reset to first page when changing sort
    }
  })
  
  currentSort.value = newSort
  currentPage.value = 1
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  applicantsStore.fetchLatestUpdatedApplicants(
    selectedTimeframe.value, 
    pageSize, 
    currentPage.value,
    currentSort.value
  )
}

// Renamed to avoid conflict with imported formatDate
const formatRelativeTime = (dateString: string) => {
  if (!dateString) return ''

  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.round(diffMs / (1000 * 60))
  const diffHours = Math.round(diffMs / (1000 * 60 * 60))
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 60) {
    return `${diffMins}m ago`
  } else if (diffHours < 24) {
    return `${diffHours}h ago`
  } else if (diffDays < 30) {
    return `${diffDays}d ago`
  } else {
    return date.toLocaleDateString()
  }
}

const getInitials = (firstName?: string, lastName?: string) => {
  const safeFirst = (firstName || '').trim()
  const safeLast = (lastName || '').trim()
  const firstInitial = safeFirst ? safeFirst.charAt(0) : ''
  const lastInitial = safeLast ? safeLast.charAt(0) : ''
  const initials = `${firstInitial}${lastInitial}`.toUpperCase()
  return initials || '—'
}

// Redirect non-guide users
onMounted(async () => {
  if (!isGuide.value) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push('/applicants')
    return
  }
  
  // Get only the relevant query parameters
  const relevantQueryParams: Record<string, string> = {}
  
  // Only keep parameters we care about
  if (route.query.timeframe) {
    relevantQueryParams.timeframe = route.query.timeframe as string
    selectedTimeframe.value = Number(route.query.timeframe)
  }
  
  if (route.query.page) {
    relevantQueryParams.page = route.query.page as string
    currentPage.value = Number(route.query.page)
  }
  
  if (route.query.ordering) {
    relevantQueryParams.ordering = route.query.ordering as string
    currentSort.value = route.query.ordering as string
  }

  if (route.query.blur === '1') {
    relevantQueryParams.blur = '1'
  }
  
  // Check if we have any unwanted query parameters
  if (Object.keys(route.query).some(key => !['timeframe', 'page', 'ordering', 'blur'].includes(key))) {
    // Replace current URL with one that only has relevant parameters
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.replace({
      path: route.path,
      query: relevantQueryParams
    })
  }
  
  try {
    await applicantsStore.fetchLatestUpdatedApplicants(
      selectedTimeframe.value,
      pageSize,
      currentPage.value,
      currentSort.value
    )
  } catch (error) {
    console.error('Failed to fetch latest updated referrals:', error)
  }
})

// Watch for page changes from URL
watch(
  () => route.query.page,
  (newPage) => {
    if (newPage) {
      const pageNum = Number(newPage)
      if (currentPage.value !== pageNum) {
        currentPage.value = pageNum
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        applicantsStore.fetchLatestUpdatedApplicants(
          selectedTimeframe.value, 
          pageSize, 
          pageNum,
          currentSort.value
        )
      }
    }
  }
)

// Watch for timeframe changes from URL
watch(
  () => route.query.timeframe,
  (newTimeframe) => {
    if (newTimeframe) {
      const timeframeValue = Number(newTimeframe)
      if (selectedTimeframe.value !== timeframeValue) {
        selectedTimeframe.value = timeframeValue
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        applicantsStore.fetchLatestUpdatedApplicants(
          timeframeValue, 
          pageSize, 
          currentPage.value,
          currentSort.value
        )
      }
    }
  }
)

// Watch for ordering changes from URL
watch(
  () => route.query.ordering,
  (newOrdering) => {
    if (newOrdering && newOrdering !== currentSort.value) {
      currentSort.value = newOrdering as string
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      applicantsStore.fetchLatestUpdatedApplicants(
        selectedTimeframe.value, 
        pageSize, 
        currentPage.value,
        currentSort.value
      )
    }
  }
)
</script>

<style scoped>
tr {
  cursor: pointer;
}

tr:hover a {
  background-color: rgba(0, 0, 0, 0.02);
}
</style>
